import React, { useState, useEffect, FormEvent } from "react";
import { useHistory, Link } from "react-router-dom";

import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Modal,
  InputGroup,
  Spinner,
  Badge,
} from "react-bootstrap";

import api from "../../services/api";

import * as S from "./styled";
import { useAuth } from "../../context/auth";
import Template from "../../template";

interface Material {
  content: string;
  name: string;
  description: string;
  plano: string;
  id: number;
}


interface UsuarioAuth {
  user?: any;
  token?: String;
  nivel?: String;
  nome?: String;
  isLoadingUser?: boolean;
}

function Materiais() {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const history = useHistory();

  const { user, isLoadingUser }: UsuarioAuth = useAuth();


  const [materiais, setMateriais] = useState<Material[]>([]);
  const [filteredMateriais, setFilteredMateriais] = useState<Material[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState<Material[]>([]);

  const [name, setName] = useState("");
  const [content, setContent] = useState("");
  const [description, setDescription] = useState("");
  const [plano, setPlano] = useState("");

  const [busca, setBusca] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [planFilter, setPlanFilter] = useState("");

  const [reload, setReload] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [materialsLoading, setMaterialsLoading] = useState(true);

  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [materialToDelete, setMaterialToDelete] = useState<Material | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  const handleDeleteClose = () => {
    setShowDeleteModal(false);
    setMaterialToDelete(null);
  };
  
  const handleDeleteShow = (material: Material) => {
    setMaterialToDelete(material);
    setShowDeleteModal(true);
  };

  // Filtrar sugestões baseadas no texto digitado
  useEffect(() => {
    if (!user) return; // Aguardar o usuário ser carregado
    
    if (busca.length > 0) {
      const filtered = materiais.filter(material => {
        const matchesSearch = material.name.toLowerCase().includes(busca.toLowerCase());
        
        // Se o usuário for admin (nível 'a'), mostrar todos os materiais
        if (user.nivel === 'a') {
          return matchesSearch;
        }
        
        // Caso contrário, mostrar apenas materiais do plano do usuário
        const matchesPlan = material.plano === user.plano;
        return matchesSearch && matchesPlan;
      });
      setFilteredSuggestions(filtered.slice(0, 5)); // Limitar a 5 sugestões
    } else {
      setFilteredSuggestions([]);
    }
  }, [busca, materiais, user]);

  // Filtrar materiais baseado na busca, categoria e plano
  useEffect(() => {
    if (!user) return; // Aguardar o usuário ser carregado
    
    let filtered = materiais;

    // Filtrar por categoria se selecionada
    if (categoryFilter) {
      filtered = filtered.filter(material => material.content === categoryFilter);
    }

    // Filtrar por plano se selecionado (apenas para admins)
    if (planFilter && user.nivel === 'a') {
      filtered = filtered.filter(material => material.plano === planFilter);
    }

    // Filtrar por busca se houver texto
    if (busca) {
      filtered = filtered.filter(material => 
        material.name.toLowerCase().includes(busca.toLowerCase()) ||
        material.description.toLowerCase().includes(busca.toLowerCase())
      );
    }

    setFilteredMateriais(filtered);
  }, [materiais, categoryFilter, planFilter, busca, user]);



  async function handleAddClasse(e: FormEvent) {
    e.preventDefault();
    setLoading(true);

    try {
      const dataClasse = {
        name: name,
        content: content,
        description: description,
        plano: plano,
      };

      let response = await api.post("/materials", dataClasse);

      handleClose();
      setName("");
      setContent("");
      setDescription("");
      setPlano("");
      setReload(response.data);
    } catch (error) {
      console.error("Erro ao adicionar material:", error);
    } finally {
      setLoading(false);
    }
  }

  async function handleBusca() {
    if (!user) return; // Aguardar o usuário ser carregado
    
    setSearchLoading(true);
    try {
      const dataBusca = {
        busca: busca,
      };

      let response = await api.post("/buscaMaterial", dataBusca);
      
      // Filtrar resultados da busca baseado no plano do usuário
      const materiaisFiltrados = response.data.filter((material: Material) => {
        // Se o usuário for admin (nível 'a'), mostrar todos os materiais
        if (user.nivel === 'a') {
          return true;
        }
        // Caso contrário, mostrar apenas materiais do plano do usuário
        return material.plano === user.plano;
      });
      
      setMateriais(materiaisFiltrados);
      setActiveFilter(busca); // Define o filtro ativo
    } catch (err) {
      console.error("Erro na busca:", err);
    } finally {
      setSearchLoading(false);
    }
  }

  function clearFilter() {
    setBusca("");
    setActiveFilter("");
    setCategoryFilter("");
    setPlanFilter("");
    setReload(Date.now().toString()); // Força recarregar todos os materiais
  }

  async function handleDeleteMaterial() {
    if (!materialToDelete) return;
    
    setDeleteLoading(true);
    try {
      await api.delete(`/deletarMaterial/${materialToDelete.id}`);
      
      // Atualizar a lista de materiais removendo o item deletado
      setMateriais(prevMateriais => 
        prevMateriais.filter(material => material.id !== materialToDelete.id)
      );
      
      handleDeleteClose();
    } catch (error) {
      console.error("Erro ao deletar material:", error);
      alert("Erro ao deletar material. Tente novamente.");
    } finally {
      setDeleteLoading(false);
    }
  }

  useEffect(() => {
    async function getClasses() {
      // Aguardar o carregamento do usuário antes de carregar materiais
      if (isLoadingUser || !user) {
        return;
      }

      setMaterialsLoading(true);
      try {
        let response = await api.get("/materials");

        // Se o usuário for admin (nível 'a'), mostrar todos os materiais sem filtrar
        // para permitir que os filtros de plano funcionem corretamente
        if (user.nivel === 'a') {
          setMateriais(response.data);
        } else {
          // Para usuários não-admin, filtrar apenas materiais do plano do usuário
          const materiaisFiltrados = response.data.filter((material: Material) => {
            return material.plano === user.plano;
          });
          setMateriais(materiaisFiltrados);
        }
      } catch (err) {
        console.error('Erro ao carregar materiais:', err);
      } finally {
        setMaterialsLoading(false);
      }
    }

    getClasses();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload, user, isLoadingUser]);

  return (
    <Template>
      <S.MainWrapper>
        <Container fluid style={{ maxWidth: '95%' }}>
            {user.nivel === "a" && (
              <S.MainContent>

                <Modal show={show} onHide={handleClose}>
                  <Modal.Header closeButton>
                    <Modal.Title>Adicionar Material</Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    <Form>
                      <Form.Group>
                        <Form.Label>Nome</Form.Label>
                        <Form.Control
                          placeholder="Nome do material"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                        />
                      </Form.Group>
                      
                      <Form.Group>
                        <Form.Label>Categoria</Form.Label>
                        <Form.Control
                          as="select"
                          value={content}
                          onChange={(e) => setContent(e.target.value)}
                        >
                          <option value="">Selecione uma categoria</option>
                          <option value="Vídeo">Vídeo</option>
                          <option value="Material">Material</option>
                        </Form.Control>
                      </Form.Group>
                      
                      <Form.Group>
                        <Form.Label>Plano</Form.Label>
                        <Form.Control
                          as="select"
                          value={plano}
                          onChange={(e) => setPlano(e.target.value)}
                        >
                          <option value="">Selecione um plano</option>
                          <option value="PRESENCIAL">PRESENCIAL</option>
                           <option value="VIP">VIP</option>
                           <option value="PLUS">PLUS</option>
                           <option value="PREMIUM">PREMIUM</option>
                          <option value="ESSENCIAL">ESSENCIAL</option>
                        </Form.Control>
                      </Form.Group>
                      
                      <Form.Group>
                        <Form.Label>Descrição</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          placeholder="Descrição do material"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                        />
                      </Form.Group>
                    </Form>
                  </Modal.Body>
                  <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                      Cancelar
                    </Button>
                    <Button 
                      variant="primary" 
                      onClick={handleAddClasse}
                      disabled={loading || !name || !content || !description || !plano}
                    >
                      {loading ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Adicionando...
                        </>
                      ) : (
                        "Adicionar"
                      )}
                    </Button>
                  </Modal.Footer>
                </Modal>
                
                {/* Modal de confirmação para deletar material */}
                <Modal show={showDeleteModal} onHide={handleDeleteClose} centered>
                  <Modal.Header closeButton>
                    <Modal.Title>Confirmar Exclusão</Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    <p>Tem certeza que deseja deletar o material <strong>"{materialToDelete?.name}"</strong>?</p>
                    <p className="text-muted">Esta ação não pode ser desfeita.</p>
                  </Modal.Body>
                  <Modal.Footer>
                    <Button variant="secondary" onClick={handleDeleteClose}>
                      Cancelar
                    </Button>
                    <Button 
                      variant="danger" 
                      onClick={handleDeleteMaterial}
                      disabled={deleteLoading}
                    >
                      {deleteLoading ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Deletando...
                        </>
                      ) : (
                        "Deletar"
                      )}
                    </Button>
                  </Modal.Footer>
                </Modal>
              </S.MainContent>
            )}

            <S.MainContent>
              <S.PageHeader>
                <S.HeaderTop>
                  <S.PageTitle>Material de Apoio</S.PageTitle>
                  <S.HeaderStats>
                    <div>
                      <S.StatNumber>{materiais.length}</S.StatNumber>
                      <br />
                      <S.StatLabel>materiais encontrados</S.StatLabel>
                    </div>
                  </S.HeaderStats>
                </S.HeaderTop>
                
                <div className="d-flex justify-content-end mb-3">
                  {busca && busca !== "" && (
                    <S.ClearButton
                      onClick={clearFilter}
                      style={{ marginRight: '10px' }}
                    >
                      Limpar
                    </S.ClearButton>
                  )}
                  {user.nivel === 'a' && (
                    <S.AddButton onClick={() => setShow(true)}>
                      ➕ Adicionar Material
                    </S.AddButton>
                  )}
                </div>
                
                <S.FiltersRow>
                  {/* Filtro de categoria - Primeiro */}
                  <S.FilterColumn>
                    <S.SearchContainer>
                      <Form.Group>
                        <Form.Label className="font-weight-bold text-dark">
                          <i className="fas fa-filter text-primary mr-1"></i>
                          Filtrar por Categoria
                        </Form.Label>
                        <InputGroup>
                          <InputGroup.Prepend>
                            <InputGroup.Text>
                              <i className="fas fa-tag"></i>
                            </InputGroup.Text>
                          </InputGroup.Prepend>
                          <Form.Control
                            as="select"
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                            className="form-control-lg"
                          >
                            <option value="">Todas as categorias</option>
                            <option value="Vídeo">Vídeo</option>
                            <option value="Material">Material</option>
                          </Form.Control>
                        </InputGroup>
                      </Form.Group>
                    </S.SearchContainer>
                  </S.FilterColumn>
                  
                  {/* Filtro de plano - Apenas para admins */}
                  {user.nivel === 'a' && (
                    <S.FilterColumn>
                      <S.SearchContainer>
                        <Form.Group>
                          <Form.Label className="font-weight-bold text-dark">
                            <i className="fas fa-crown text-primary mr-1"></i>
                            Filtrar por Plano
                          </Form.Label>
                          <InputGroup>
                            <InputGroup.Prepend>
                              <InputGroup.Text>
                                <i className="fas fa-star"></i>
                              </InputGroup.Text>
                            </InputGroup.Prepend>
                            <Form.Control
                              as="select"
                              value={planFilter}
                              onChange={(e) => setPlanFilter(e.target.value)}
                              className="form-control-lg"
                            >
                              <option value="">Todos os planos</option>
                              <option value="PRESENCIAL">PRESENCIAL</option>
                              <option value="VIP">VIP</option>
                              <option value="PLUS">PLUS</option>
                              <option value="PREMIUM">PREMIUM</option>
                              <option value="ESSENCIAL">ESSENCIAL</option>
                            </Form.Control>
                          </InputGroup>
                        </Form.Group>
                      </S.SearchContainer>
                    </S.FilterColumn>
                  )}
                  
                  {/* Filtro de busca - Segundo */}
                  <S.FilterColumn>
                    <S.SearchContainer>
                      <Form.Group className="dropdown-container">
                        <Form.Label className="font-weight-bold text-dark">
                          <i className="fas fa-book text-primary mr-1"></i>
                          Buscar Material
                        </Form.Label>
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '20px' }}>
                          <InputGroup style={{ flex: 1 }}>
                            <InputGroup.Prepend>
                              <InputGroup.Text>
                                <i className="fas fa-search"></i>
                              </InputGroup.Text>
                            </InputGroup.Prepend>
                            <Form.Control
                              type="text"
                              placeholder="Digite o nome do material..."
                              value={busca}
                              onChange={(e) => {
                                setBusca(e.target.value);
                                setShowDropdown(true);
                              }}
                              onFocus={() => setShowDropdown(true)}
                              onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                              className="form-control-lg"
                            />
                          </InputGroup>
                          <S.SearchButton 
                            onClick={handleBusca}
                            disabled={searchLoading}
                          >
                            {searchLoading ? (
                              <>
                                <div className="spinner-border spinner-border-sm mr-2" role="status">
                                  <span className="sr-only">Carregando...</span>
                                </div>
                                Buscando...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-search mr-1"></i>
                                Buscar
                              </>
                            )}
                          </S.SearchButton>
                          {showDropdown && filteredSuggestions.length > 0 && (
                            <S.DropdownList>
                              {filteredSuggestions.map((material, index) => (
                                 <S.DropdownItem
                                   key={material.id}
                                   onClick={() => {
                                     setBusca(material.name);
                                     setShowDropdown(false);
                                     setFilteredSuggestions([]);
                                     // Fazer busca específica para o item selecionado
                                     const searchSpecific = async () => {
                                       setSearchLoading(true);
                                       try {
                                         const dataBusca = {
                                           busca: material.name,
                                         };
                                         let response = await api.post("/buscaMaterial", dataBusca);
                                         setMateriais(response.data);
                                         setActiveFilter(material.name); // Define o filtro ativo
                                       } catch (err) {
                                         console.error("Erro na busca:", err);
                                       } finally {
                                         setSearchLoading(false);
                                       }
                                     };
                                     searchSpecific();
                                   }}
                                 >
                                   {material.name}
                                 </S.DropdownItem>
                               ))}
                            </S.DropdownList>
                          )}
                        </div>
                      </Form.Group>
                    </S.SearchContainer>
                  </S.FilterColumn>
                </S.FiltersRow>
                
                {/* Indicador de filtro ativo */}
                {(activeFilter || categoryFilter || planFilter) && (
                  <S.ActiveFilterContainer>
                    <S.FilterLabel>Filtrando por:</S.FilterLabel>
                    {activeFilter && (
                      <S.ActiveFilterBadge>
                        <i className="fas fa-search mr-1"></i>
                        {activeFilter}
                        <S.ClearFilterButton onClick={() => {
                          setBusca("");
                          setActiveFilter("");
                          setReload(Date.now().toString()); // Força recarregar todos os materiais
                        }} title="Limpar busca">
                          <i className="fas fa-times"></i>
                        </S.ClearFilterButton>
                      </S.ActiveFilterBadge>
                    )}
                    {categoryFilter && (
                      <S.ActiveFilterBadge>
                        <i className="fas fa-tag mr-1"></i>
                        {categoryFilter}
                        <S.ClearFilterButton onClick={() => {
                          setCategoryFilter("");
                          if (!busca && !planFilter) {
                            setReload(Date.now().toString()); // Força recarregar todos os materiais se não há busca ativa
                          }
                        }} title="Limpar categoria">
                          <i className="fas fa-times"></i>
                        </S.ClearFilterButton>
                      </S.ActiveFilterBadge>
                    )}
                    {planFilter && user.nivel === 'a' && (
                      <S.ActiveFilterBadge>
                        <i className="fas fa-star mr-1"></i>
                        {planFilter}
                        <S.ClearFilterButton onClick={() => {
                          setPlanFilter("");
                          if (!busca && !categoryFilter) {
                            setReload(Date.now().toString()); // Força recarregar todos os materiais se não há busca ativa
                          }
                        }} title="Limpar plano">
                          <i className="fas fa-times"></i>
                        </S.ClearFilterButton>
                      </S.ActiveFilterBadge>
                    )}
                  </S.ActiveFilterContainer>
                )}
              </S.PageHeader>
            </S.MainContent>

            <S.MainContent>

              {materialsLoading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                  <Spinner animation="border" role="status">
                    <span className="sr-only">Carregando materiais...</span>
                  </Spinner>
                  <span className="ml-2">Carregando materiais...</span>
                </div>
              ) : filteredMateriais && filteredMateriais.length > 0 ? (
                <Row>
                  {filteredMateriais.map((material, idx) => (
                    <Col key={idx} xs={12} sm={6} md={4} lg={3} xl={3}>
                      <S.MaterialCard>
                        <S.MaterialHeader>
                          <S.MaterialIcon>
                            {material.content === 'Vídeo' ? (
                              <S.VideoIcon />
                            ) : material.content === 'Material' ? (
                              <S.BookOpenIcon />
                            ) : (
                              <S.BookOpenIcon />
                            )}
                          </S.MaterialIcon>
                          
                          <S.MaterialBadge 
                            className={
                              material.content === 'Vídeo' ? 'video' :
                              material.content === 'Material' ? 'material' :
                              'default'
                            }
                          >
                            {material.content === 'Vídeo' ? 'VÍDEO' :
                             material.content === 'Material' ? 'MATERIAL' :
                             material.content || 'MATERIAL'}
                          </S.MaterialBadge>
                          
                          <br />
                          
                          {material.plano && (
                            <Badge 
                              variant="info" 
                              style={{
                                fontSize: '10px',
                                marginTop: '0px',
                                padding: '4px 20px',
                                backgroundColor: '#17a2b8',
                                color: 'white'
                              }}
                            >
                              {material.plano}
                            </Badge>
                          )}
                          
                          <S.MaterialTitle>{material.name}</S.MaterialTitle>
                        </S.MaterialHeader>

                        <S.MaterialContent>
                          <S.MaterialDescription>
                            {material.description}
                          </S.MaterialDescription>

                          <S.MaterialActions>
                            <Link
                              to={`/material/${material.id}`}
                              style={{textDecoration: 'none', width: '100%'}}
                            >
                              <S.MaterialButton variant="primary">
                                <S.DownloadIcon />
                                Visualizar Material
                              </S.MaterialButton>
                            </Link>
                            
                            {user.nivel === 'a' && (
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => handleDeleteShow(material)}
                                style={{
                                  marginTop: '8px',
                                  width: '100%',
                                  fontSize: '12px',
                                  padding: '6px 12px'
                                }}
                              >
                                🗑️ Deletar Material
                              </Button>
                            )}
                          </S.MaterialActions>
                        </S.MaterialContent>
                      </S.MaterialCard>
                    </Col>
                  ))}
                </Row>
              ) : (
                <Row>
                  <Col className="text-center">
                    {!materialsLoading && <p>Nenhum material encontrado</p>}
                  </Col>
                </Row>
              )}
            </S.MainContent>
        </Container>
      </S.MainWrapper>
    </Template>
  );
}

export default Materiais;
